import { useEffect, useState } from "react";
import { Subscriptions, getAuth } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BellPlus, BellRing, Loader2 } from "lucide-react";
import { toast, confirmDialog } from "@/components/ui/dialog";
import { navigate } from "@/lib/router";

type Status = "loading" | "subscribed" | "unsubscribed" | "limit" | "disabled" | "anon";

export function QuickSubscribeButton({
  keyword, categoryId, size = "sm",
}: { keyword: string; categoryId?: string; size?: "sm" | "default" }) {
  const [status, setStatus] = useState<Status>("loading");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    if (!getAuth()) { setStatus("anon"); return; }
    try {
      const r = await Subscriptions.check(keyword, categoryId);
      if (r.rule_id) setStatus("subscribed");
      else if (!r.can_subscribe) setStatus(r.rules_used >= r.rules_limit ? "limit" : "disabled");
      else setStatus("unsubscribed");
    } catch {
      setStatus("disabled");
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [keyword, categoryId]);

  const subscribe = async () => {
    if (status === "anon") { navigate("/login"); return; }
    if (status !== "unsubscribed") return;
    const ok = await confirmDialog({
      title: "确认订阅该关键词？",
      description: `命中关键词「${keyword}」的新公告（按标题/标段匹配）会自动按你已配置的通知通道推送。可在「我的 → 订阅推送」中随时关闭或调整。`,
      confirmText: "确认订阅",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await Subscriptions.createRule({
        keyword,
        category_id: categoryId || null as any,
        match_fields: ["title", "bid_sections"] as any,
        is_active: true,
      });
      toast.success(`已订阅关键词「${keyword}」`);
      setStatus("subscribed");
    } catch (e: any) {
      toast.error(e?.body?.message || e?.message || "订阅失败，请稍后重试");
    } finally {
      setBusy(false);
    }
  };

  if (status === "loading") {
    return <Button size={size} variant="ghost" disabled><Loader2 className="h-3.5 w-3.5 animate-spin" /></Button>;
  }
  if (status === "subscribed") {
    return (
      <Button size={size} variant="ghost" className="text-emerald-600 cursor-default" type="button" disabled>
        <BellRing className="h-3.5 w-3.5 mr-1" /> 已订阅
      </Button>
    );
  }
  if (status === "limit") {
    return (
      <Button size={size} variant="ghost" disabled title="订阅规则数量已达上限，请到「订阅推送」管理"
              type="button">
        已达上限
      </Button>
    );
  }
  if (status === "disabled") {
    return null;
  }
  return (
    <Button size={size} variant="outline" onClick={subscribe} disabled={busy} type="button"
            title={status === "anon" ? "登录后即可一键订阅" : "命中此关键词的新公告将自动推送"}>
      {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <BellPlus className="h-3.5 w-3.5 mr-1" />}
      {status === "anon" ? "登录订阅" : "+ 订阅"}
    </Button>
  );
}
